import { z } from 'zod/v3';
import { ok } from 'neverthrow';
import React from 'react';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';
import { AgentModel } from '../agent/agent.model';
import type { ActionTrackingContext } from '../actions/framework/action-tracking';
import { Routes } from '../routes';
import { GraphModel } from './graph.model';
import { NewToolModal } from '../tool/new-tool-modal';

type Params = {
  mentionType: 'tool' | 'document' | 'expr';
  mentionName: string;
  /** For tools, the tool key to navigate to */
  toolKey?: string;
};

export class NavigateToMentionAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Navigate to Mention';
  description =
    'Navigate to a tool, document, or expression from an agent mention';
  inputSchema = z.object({
    mentionType: z.enum(['tool', 'document', 'expr']),
    mentionName: z.string(),
    toolKey: z.string().optional(),
  });
  discrete = true;

  constructor(
    private readonly tabManager: {
      push: (props: { route: string }) => void;
    },
    private readonly modalManager?: {
      push: (opts: {
        view: () => React.ReactNode;
        size?: 'auto' | 'lg';
      }) => void;
      close: () => void;
    }
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.isInScope(GraphModel);
  }

  async execute(
    data: Params,
    _context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    // For tools, open the tool modal instead of navigating
    if (data.mentionType === 'tool' && this.modalManager) {
      this.modalManager.push({
        view: () =>
          React.createElement(NewToolModal, {
            onComplete: async () => {
              // Tool was selected/configured - close modal
            },
            onClose: () => this.modalManager!.close(),
            initialToolKey: data.toolKey,
          }),
        size: 'lg',
      });
      return ok({});
    }

    let route = '';

    switch (data.mentionType) {
      case 'tool':
        route = Routes.tool.forId(data.mentionName).toString();
        break;
      case 'document':
        route = Routes.document.forId(data.mentionName).toString();
        break;
      case 'expr':
        route = Routes.repl.forId(data.mentionName).toString();
        break;
    }
    this.tabManager.push({ route });
    return ok({});
  }

  track({
    payload,
    context,
  }: ActionTrackingContext<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const agent = context.targetedModelOfType(AgentModel);
    if (agent === null) {
      return null;
    }

    return {
      event: 'agent_mention_navigated',
      properties: {
        agentId: agent.id,
        agentName: agent.name.snapshot(),
        mentionType: payload.mentionType,
        mentionName: payload.mentionName,
        currentTab: agent.currentTab.snapshot(),
        isNew: agent.isNew,
      },
    };
  }
}
